/**
 * CAD 会话 API 服务。
 * 封装 OpenProject/GetCadNodeTree unary RPC 与 GetCommitFile 文件流调用。
 */
import { Code, ConnectError, createClient } from '@connectrpc/connect'
import { create } from '@bufbuild/protobuf'
import {
  CadService,
  OpenProjectRequestSchema,
  GetCommitFileRequestSchema,
  GetCadNodeTreeRequestSchema,
  VersionControlService,
} from '@puppycad/proto'
import type {
  GetCadNodeTreeResponse,
  OpenProjectResponse,
} from '@puppycad/proto'
import { useAuthStore } from '@/stores/auth'
import { logger } from '@/utils'
import { AuthReplaySession } from '@/services/auth-replay'
import { buildVersionFileUrl } from '@/services/project-model-loading/version-file-url-builder'
import { apiConfig } from './config'
import { grpcWebTransport } from './transport/grpc-web-transport'
import {
  normalizeOpenProjectSessionSource,
} from './open-project-input'
import type {
  OpenProjectRequestInput,
  OpenProjectSessionSource,
} from './open-project-input'

const cadClient = createClient(CadService, grpcWebTransport)
const versionClient = createClient(VersionControlService, grpcWebTransport)
const GET_FILE_TRANSIENT_RETRY_LIMIT = 1
const GET_FILE_RUNTIME_UUID_PREFIXES = [
  'open-project:',
  'open-project-node:',
  'insert:',
  'inline:',
  'inline-assembly:',
  'checkout-commit:',
] as const

export interface OpenProjectDownloadFileContext {
  /** 当前文件在所属仓库提交内的真实路径。 */
  readonly fileId: string
  /** 当前文件后端业务 UUID；前端运行时 UUID 会在请求前过滤。 */
  readonly fileUuid?: string
  /** 当前文件所属仓库 ID。 */
  readonly repositoryId: bigint
  /** 当前文件所属提交 ID。 */
  readonly commitId: string
  /** 当前文件所属项目 ID。 */
  readonly projectId?: bigint
  /** 当前文件所属分支 ID。 */
  readonly branchId?: bigint
}

export interface OpenProjectDownloadContext {
  /** 当前会话对应的仓库 ID。 */
  readonly repositoryId: bigint
  /** 当前会话打开的提交 ID。 */
  readonly commitId: string
  /** 当前会话对应的项目 ID。 */
  readonly projectId?: bigint
  /** 当前会话对应的分支 ID。 */
  readonly branchId?: bigint
  /** 当前上下文下可按文件精确路由的仓库提交上下文。 */
  readonly files?: readonly OpenProjectDownloadFileContext[]
}

interface RegisteredOpenProjectDownloadContext {
  /** 找不到文件级上下文时使用的根仓库提交上下文。 */
  readonly defaultContext: OpenProjectDownloadContext
  /** 按仓库内真实路径索引的文件上下文。 */
  readonly fileContextByFileId: ReadonlyMap<string, OpenProjectDownloadFileContext>
  /** 按后端真实业务 UUID 索引的文件上下文。 */
  readonly fileContextByFileUuid: ReadonlyMap<string, OpenProjectDownloadFileContext>
}

interface ResolvedOpenProjectDownloadContext {
  /** 实际进入 GetCommitFile 的仓库 ID。 */
  readonly repositoryId: bigint
  /** 实际进入 GetCommitFile 的提交 ID。 */
  readonly commitId: string
  /** 实际进入 GetCommitFile 的仓库内路径。 */
  readonly fileId: string
}

const openProjectDownloadContexts = new Map<string, RegisteredOpenProjectDownloadContext>()

/**
 * 创建 OpenProject unary 请求。
 *
 * 设计说明：`commitId` 已由正式 proto 字段承载；为空字符串表示让后端打开目标分支 HEAD，
 * 这样历史预览和当前编辑态都复用同一个 OpenProject 入口。
 *
 * @param input OpenProject 请求上下文。
 * @returns 可直接传给 `CadService.OpenProject` 的请求消息。
 */
export function createOpenProjectRequest(input: OpenProjectRequestInput) {
  return create(OpenProjectRequestSchema, {
    projectId: input.projectId,
    ...(input.repositoryId !== undefined ? { repositoryId: input.repositoryId } : {}),
    ...(input.branchId !== undefined ? { branchId: input.branchId } : {}),
    commitId: input.commitId?.trim() ?? '',
  })
}

/**
 * OpenProject unary 请求的调用选项。
 */
export interface OpenProjectSessionRequestOptions {
  /** 调用方控制本次 OpenProject unary 请求生命周期的取消信号。 */
  readonly signal?: AbortSignal
}

export interface GetCadNodeTreeSessionInput {
  /** 需要重新加载的节点仓库 ID。 */
  readonly repositoryId: bigint
  /** 目标分支 ID；0 表示交给后端按默认分支解析。 */
  readonly branchId?: bigint
  /** 目标提交 ID；空字符串表示读取分支 HEAD。 */
  readonly commitId?: string
}

/**
 * 创建 GetCadNodeTree unary 请求。
 *
 * @param input 节点树读取上下文。
 * @returns 可直接传给 `CadService.GetCadNodeTree` 的请求消息。
 */
export function createGetCadNodeTreeRequest(input: GetCadNodeTreeSessionInput) {
  return create(GetCadNodeTreeRequestSchema, {
    repositoryId: input.repositoryId,
    branchId: input.branchId ?? 0n,
    commitId: input.commitId?.trim() ?? '',
  })
}

/**
 * 注册当前 OpenProject 会话的文件下载上下文。
 *
 * @param downloadContextId 前端生成的下载上下文 ID。
 * @param context 当前打开仓库和提交信息。
 */
export function registerOpenProjectDownloadContext(
  downloadContextId: string,
  context: OpenProjectDownloadContext,
): void {
  const normalizedContextId = downloadContextId.trim()
  if (!normalizedContextId) {
    return
  }
  openProjectDownloadContexts.set(normalizedContextId, createRegisteredOpenProjectDownloadContext(context))
}

/**
 * 移除当前 OpenProject 会话的文件下载上下文。
 *
 * @param downloadContextId 前端生成的下载上下文 ID。
 */
export function unregisterOpenProjectDownloadContext(downloadContextId: string): void {
  const normalizedContextId = downloadContextId.trim()
  if (!normalizedContextId) {
    return
  }
  openProjectDownloadContexts.delete(normalizedContextId)
}

/**
 * 归一化下载上下文索引用的仓库内路径。
 *
 * 设计说明：这里不能调用 `normalizeCommitFilePath`，因为索引阶段允许空路径；
 * 真正发请求前再根据 UUID 兜底规则判断是否允许为空。
 *
 * @param fileId 文件路径 ID。
 * @returns 可用于 Map 索引的路径 key。
 */
function normalizeDownloadContextFileId(fileId: string | undefined): string {
  return fileId?.trim().replace(/^\/+/u, '') ?? ''
}

/**
 * 预构建下载上下文索引。
 *
 * 设计说明：
 * 1. OpenProject 根节点和子模块节点都可能返回文件；文件下载必须落到各自所属仓库提交；
 * 2. UUID 比路径更能表达真实业务身份，因此后续解析时优先按 UUID 命中；
 * 3. 同一路径重复时保留最早注册项，避免子模块常见的 `_assembly.json` 覆盖根仓库文件。
 *
 * @param context OpenProject 或历史提交注册的下载上下文。
 * @returns 可供下载时快速查找的上下文条目。
 */
function createRegisteredOpenProjectDownloadContext(
  context: OpenProjectDownloadContext,
): RegisteredOpenProjectDownloadContext {
  const fileContextByFileId = new Map<string, OpenProjectDownloadFileContext>()
  const fileContextByFileUuid = new Map<string, OpenProjectDownloadFileContext>()

  context.files?.forEach((fileContext) => {
    const normalizedFileId = normalizeDownloadContextFileId(fileContext.fileId)
    if (normalizedFileId && !fileContextByFileId.has(normalizedFileId)) {
      fileContextByFileId.set(normalizedFileId, fileContext)
    }

    const normalizedFileUuid = resolveGetFileRequestFileUuid(fileContext.fileUuid ?? '')
    if (normalizedFileUuid && !fileContextByFileUuid.has(normalizedFileUuid)) {
      fileContextByFileUuid.set(normalizedFileUuid, fileContext)
    }
  })

  return {
    defaultContext: context,
    fileContextByFileId,
    fileContextByFileUuid,
  }
}

/**
 * 为 OpenProject 返回的仓库提交上下文生成稳定下载上下文 ID。
 *
 * 设计说明：
 * 1. 最新 OpenProjectResponse 已不再返回后端 sessionId，模型下载必须由前端持有上下文；
 * 2. ID 中包含项目、仓库、分支和提交，能让 loader 的现有 sessionId 形态继续作为缓存版本兜底；
 * 3. commitId 缺失时只生成占位 ID，真正下载仍由 GetCommitFile 边界抛出明确错误；
 * 4. 该 ID 只用于前端下载上下文查找，不会发送给后端。
 *
 * @param context 当前打开仓库和提交信息。
 * @returns 可传给 downloadCadFile 的下载上下文 ID。
 */
export function createOpenProjectDownloadContextId(context: OpenProjectDownloadContext): string {
  const commitId = context.commitId.trim() || 'no-commit'
  const projectId = context.projectId?.toString() ?? '0'
  const branchId = context.branchId?.toString() ?? '0'
  return `open-project-download:${projectId}:${context.repositoryId.toString()}:${branchId}:${commitId}`
}

/**
 * 读取当前会话的提交文件下载上下文。
 *
 * @param downloadContextId 前端生成的下载上下文 ID。
 * @param fileId 调用方准备下载的文件路径。
 * @param fileUuid 调用方准备下载的后端业务 UUID。
 * @returns GetCommitFile 所需的仓库和提交信息。
 */
function resolveOpenProjectDownloadContext(
  downloadContextId: string,
  fileId: string,
  fileUuid: string,
): ResolvedOpenProjectDownloadContext {
  const normalizedContextId = downloadContextId.trim()
  const registeredContext = openProjectDownloadContexts.get(normalizedContextId)
  if (!registeredContext) {
    throw new Error(`GetCommitFile 缺少 OpenProject 下载上下文：downloadContextId=${downloadContextId}`)
  }

  const normalizedFileId = normalizeDownloadContextFileId(fileId)
  const fileContextByFileId = normalizedFileId
    ? registeredContext.fileContextByFileId.get(normalizedFileId)
    : undefined
  const normalizedFileUuid = resolveGetFileRequestFileUuid(fileUuid)
  const fileContextByUuid = normalizedFileUuid
    ? registeredContext.fileContextByFileUuid.get(normalizedFileUuid)
    : undefined
  const context = fileContextByFileId ?? fileContextByUuid ?? registeredContext.defaultContext

  if (!context.commitId.trim()) {
    throw new Error(`GetCommitFile 缺少 commitId：downloadContextId=${downloadContextId}`)
  }

  return {
    repositoryId: context.repositoryId,
    commitId: context.commitId,
    fileId: 'fileId' in context ? context.fileId : fileId,
  }
}

/**
 * 校验 OpenProject unary 请求是否仍允许继续执行。
 *
 * 设计说明：Connect unary 的取消会通过 signal 传播到底层 transport；这里额外在请求前后校验，
 * 避免已取消请求继续进入认证重试、日志记录或响应消费流程。
 *
 * @param signal 调用方传入的取消信号。
 */
function assertOpenProjectSessionSignalActive(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new ConnectError('OpenProject 请求已取消', Code.Canceled)
  }
}

export async function openProjectSession(
  source: OpenProjectSessionSource,
  options: OpenProjectSessionRequestOptions = {},
): Promise<OpenProjectResponse> {
  const signal = options.signal
  assertOpenProjectSessionSignalActive(signal)
  const input = normalizeOpenProjectSessionSource(source)
  const request = createOpenProjectRequest(input)
  const response = signal
    ? await cadClient.openProject(request, { signal })
    : await cadClient.openProject(request)
  assertOpenProjectSessionSignalActive(signal)
  return response
}

/**
 * 读取指定仓库、分支和提交下的完整 CAD 节点子树。
 *
 * 设计说明：子节点版本切换不再调用“采纳版本写接口”，而是先用目标提交重载
 * 该子节点的完整快照，再由本地装配草稿决定是否替换当前工作装配表达。
 *
 * @param input 节点树重载请求上下文。
 * @returns 后端返回的节点树响应。
 */
export async function getCadNodeTreeSession(
  input: GetCadNodeTreeSessionInput,
): Promise<GetCadNodeTreeResponse> {
  const request = createGetCadNodeTreeRequest(input)
  logger.info('[cad-session] GetCadNodeTree 最终传值', {
    repositoryId: input.repositoryId.toString(),
    branchId: input.branchId?.toString() ?? '0',
    commitId: input.commitId?.trim() ?? '',
  })
  return await cadClient.getCadNodeTree(request)
}

/**
 * 将响应体转换为文件字节。
 *
 * @param response HTTP 下载响应。
 * @returns 完整文件字节。
 */
async function readHttpResponseBytes(response: Response): Promise<Uint8Array> {
  const buffer = await response.arrayBuffer()

  return new Uint8Array(buffer)
}

/**
 * 将内存中的 access token 转为后端要求的 Authorization header。
 *
 * @param token 当前 access token。
 * @returns 可直接写入 HTTP header 的鉴权值。
 */
function buildBearerAuthorizationHeader(token: string): string {
  const normalizedToken = token.trim()

  if (!normalizedToken) {
    throw new Error('version-file 下载缺少 Authorization token')
  }

  return `Bearer ${normalizedToken}`
}

/**
 * 获取当前版本文件下载所需的 Authorization header。
 *
 * 旧 gRPC-Web 传输层会自动追加鉴权 header；现在兼容下载入口改为 HTTP 版本文件接口，
 * 因此必须在 API 边界显式读取 token，避免 loader 层感知认证细节。
 *
 * @returns 可用于 `/download/version-file` 的 Authorization header。
 */
export async function resolveVersionFileAuthorizationHeader(): Promise<string> {
  const authStore = useAuthStore()
  const currentToken = authStore.getAccessToken()

  if (currentToken?.trim()) {
    return buildBearerAuthorizationHeader(currentToken)
  }

  return buildBearerAuthorizationHeader(await authStore.refreshSession())
}

/**
 * 重新刷新 token 并生成版本文件下载 Authorization header。
 *
 * @returns 刷新后的 Authorization header。
 */
export async function refreshVersionFileAuthorizationHeader(): Promise<string> {
  const authStore = useAuthStore()

  return buildBearerAuthorizationHeader(await authStore.refreshSession())
}

/**
 * 执行一次 HTTP 版本文件下载。
 *
 * @param downloadContext 已解析出的仓库、提交和路径上下文。
 * @param authorizationHeader 当前 Authorization header。
 * @returns 下载到的文件字节。
 */
async function readVersionFileHttpOnce(
  downloadContext: ResolvedOpenProjectDownloadContext,
  authorizationHeader: string,
): Promise<{ readonly bytes?: Uint8Array; readonly unauthorized: boolean }> {
  const url = buildVersionFileUrl({
    backendBaseUrl: apiConfig.baseUrl,
    repositoryId: downloadContext.repositoryId.toString(),
    commitId: downloadContext.commitId,
    fileId: downloadContext.fileId,
  })
  const response = await fetch(url, {
    headers: {
      Authorization: authorizationHeader,
    },
  })

  if (response.status === 401) {
    return {
      unauthorized: true,
    }
  }

  if (!response.ok) {
    throw new Error(
      `version-file 下载失败：repositoryId=${downloadContext.repositoryId.toString()}, commitId=${downloadContext.commitId}, path=${downloadContext.fileId}, status=${response.status}`,
    )
  }

  return {
    bytes: await readHttpResponseBytes(response),
    unauthorized: false,
  }
}

/**
 * 通过 `/download/version-file` 读取提交文件。
 *
 * 这是旧 loader 兼容入口的下载层替换：loader 仍可按原签名调用 `downloadCadFile`，
 * 但有路径的版本文件不再向后端发送 `fileUuid`，避免 `path` 与 `file_uuid` 不一致导致
 * `file_uuid mismatch`。
 *
 * @param downloadContext 已解析出的仓库、提交和路径上下文。
 * @returns 下载到的文件字节。
 */
async function readVersionFileHttp(
  downloadContext: ResolvedOpenProjectDownloadContext,
): Promise<Uint8Array> {
  let authorizationHeader = await resolveVersionFileAuthorizationHeader()
  const replaySession = new AuthReplaySession({
    requestKind: 'version-file',
    operation: downloadContext.fileId,
    fileId: downloadContext.fileId,
  })

  while (true) {
    const result = await readVersionFileHttpOnce(downloadContext, authorizationHeader)

    if (!result.unauthorized) {
      if (!result.bytes) {
        throw new Error('version-file 下载响应缺少文件字节')
      }
      return result.bytes
    }

    replaySession.recordUnauthorizedAndAssertCanReplay({
      status: 401,
      cause: new Error(
        `version-file 下载认证失败：repositoryId=${downloadContext.repositoryId.toString()}, commitId=${downloadContext.commitId}, path=${downloadContext.fileId}`,
      ),
    })

    authorizationHeader = await replaySession.refreshAuthorization(refreshVersionFileAuthorizationHeader)
  }
}

export async function downloadCadFile(
  downloadContextId: string,
  fileId: string,
  fileUuid: string,
): Promise<Uint8Array> {
  const requestFileUuid = resolveGetFileRequestFileUuid(fileUuid)
  const downloadContext = resolveOpenProjectDownloadContext(downloadContextId, fileId, requestFileUuid)
  const normalizedPath = normalizeCommitFilePath(downloadContext.fileId, requestFileUuid)

  if (normalizedPath) {
    return readVersionFileHttp({
      ...downloadContext,
      fileId: normalizedPath,
    })
  }

  for (let retry = 0; retry <= GET_FILE_TRANSIENT_RETRY_LIMIT; retry += 1) {
    try {
      return await readCadFileStreamOnce(downloadContext, requestFileUuid)
    } catch (error) {
      if (retry < GET_FILE_TRANSIENT_RETRY_LIMIT && isRetryableGetFileTransportError(error)) {
        continue
      }
      throw error
    }
  }
  throw new Error('GetCommitFile 重试流程异常结束')
}

/**
 * 解析真正应该发给后端提交文件下载接口的文件 UUID。
 *
 * 设计说明：
 * 1. OpenProject/Insert/Inline 等前端运行时 UUID 只用于本地缓存身份，不能发送给后端；
 * 2. 后端真实 external_file_uuid 可以在 fileId 为空时独立定位文件，因此必须保留；
 * 3. 这里仅过滤明确的前端运行时前缀，避免误丢后端后续扩展的业务 UUID 格式。
 *
 * @param fileUuid 调用方传入的文件 UUID 或前端运行时身份。
 * @returns 可进入 GetCommitFileRequest.fileUuid 的后端 UUID。
 */
function resolveGetFileRequestFileUuid(fileUuid: string): string {
  const normalizedFileUuid = fileUuid.trim()
  if (!normalizedFileUuid) {
    return ''
  }

  const isRuntimeUuid = GET_FILE_RUNTIME_UUID_PREFIXES.some((prefix) => (
    normalizedFileUuid.startsWith(prefix)
  ))
  return isRuntimeUuid ? '' : normalizedFileUuid
}

/**
 * 执行单次 GetCommitFile 流式下载。
 *
 * 设计说明：
 * 1. 字节拼接保持在底层会话 API 内部，避免 loader 分散处理 chunk 语义；
 * 2. 上层 `downloadCadFile` 负责重试策略，本函数只表达单次 RPC 调用；
 * 3. GetCommitFile 需要仓库提交上下文，因此这里通过 OpenProject 注册的下载上下文补齐。
 *
 * @param downloadContextId 前端生成的下载上下文 ID。
 * @param fileId 文件路径 ID，可为空。
 * @param fileUuid 后端文件 UUID 或前端运行时身份。
 * @returns 完整文件字节。
 */
async function readCadFileStreamOnce(
  downloadContext: ResolvedOpenProjectDownloadContext,
  requestFileUuid: string,
): Promise<Uint8Array> {
  const chunks: Uint8Array[] = []
  let totalSize = 0n

  const request = create(GetCommitFileRequestSchema, {
    repositoryId: downloadContext.repositoryId,
    commitId: downloadContext.commitId,
    path: normalizeCommitFilePath(downloadContext.fileId, requestFileUuid),
    fileUuid: requestFileUuid,
  })

  try {
    const chunkStream = versionClient.getCommitFile(request)

    for await (const resp of chunkStream) {
      if (resp.totalSize > 0n) {
        totalSize = resp.totalSize
      }
      if (resp.chunk.length > 0) {
        // 流式响应的 chunk 可能复用底层缓冲区，这里复制后再进入拼接队列。
        chunks.push(new Uint8Array(resp.chunk))
      }
    }

    const fallbackSize = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
    const resultSize = totalSize > 0n ? Number(totalSize) : fallbackSize
    const result = new Uint8Array(resultSize)
    let offset = 0
    for (const chunk of chunks) {
      result.set(chunk, offset)
      offset += chunk.length
    }

    return result
  } catch (error) {
    throw error
  }
}

/**
 * 归一化提交内文件路径。
 *
 * @param fileId OpenProject 返回的文件 ID。
 * @param fileUuid 后端真实业务 UUID；存在时允许路径为空。
 * @returns GetCommitFile 使用的仓库相对路径。
 */
function normalizeCommitFilePath(fileId: string, fileUuid: string): string {
  const normalizedPath = fileId.trim().replace(/^\/+/u, '')
  if (!normalizedPath) {
    if (fileUuid.trim()) {
      return ''
    }
    throw new Error('GetCommitFile 缺少文件路径')
  }
  return normalizedPath
}

/**
 * 判断 GetCommitFile 是否遇到可重试的传输层关闭。
 *
 * 设计说明：
 * 1. `Response closed without grpc-status (Headers only)` 是 gRPC-Web 连接在响应头阶段关闭的传输错误；
 * 2. GetCommitFile 是只读幂等请求，立即重试一次可以消除刷新时偶发连接复用失败；
 * 3. NOT_FOUND、权限、业务错误不重试，避免掩盖真实后端状态。
 *
 * @param error 捕获到的错误。
 * @returns 是否允许重试。
 */
function isRetryableGetFileTransportError(error: unknown): boolean {
  return error instanceof ConnectError
    && error.code === Code.Unknown
    && error.message.includes('Response closed without grpc-status')
}
